import {
  EnvelopeListResponse,
  type EnvelopeState,
  type EnvelopeSummary,
} from '@signbento/contracts'
import type { MessageKey, Tone } from '@signbento/ui'
import { ApiError, type ApiTransport } from './api-client.js'
import type { LocalLifecycle } from './document-lifecycle.js'
import { requestStatusWord } from './status-words.js'

/**
 * Agreements views (UX-13, decided 2026-10-06): one list for what is on this device and what was
 * sent from it, cut by views rather than separate pages, so navigation stays at five entries
 * (ADD-055). Local documents are read from the vault and never leave it; requests are read from the
 * account's envelope list, which is what both delivery modes (managed and manual) create.
 *
 * All really is all: this device's documents and every request the account sent. Needs attention
 * comes straight after it, because it is the one view that asks the person to do something; the
 * page hides it while it is empty.
 */
export const AGREEMENT_VIEWS = [
  'all',
  'attention',
  'waiting',
  'drafts',
  'completed',
  'closed',
] as const
export type AgreementView = (typeof AGREEMENT_VIEWS)[number]

export const AGREEMENT_VIEW_LABEL: Readonly<Record<AgreementView, MessageKey>> = {
  all: 'agreements.view_all',
  drafts: 'agreements.view_drafts',
  // The same words as Home's compartment and the request status, so one state has one name.
  waiting: 'status.waiting',
  completed: 'agreements.view_completed',
  closed: 'agreements.view_closed',
  attention: 'agreements.view_attention',
}

export function parseAgreementView(value: string | null): AgreementView {
  return AGREEMENT_VIEWS.find((view) => view === value) ?? 'all'
}

/** The local lifecycle a view narrows the device list to; `all` keeps every document. */
export function viewLifecycle(view: AgreementView): LocalLifecycle | null {
  if (view === 'drafts') return 'draft'
  if (view === 'completed') return 'completed'
  return null
}

/** Whether the view shows this device's documents. */
export function viewShowsLocal(view: AgreementView): boolean {
  return view === 'all' || view === 'drafts' || view === 'completed'
}

/** Every state a sent request can be in; a local draft is never a request yet. */
export const REQUEST_STATES: readonly EnvelopeState[] = [
  'PREPARING',
  'ACTIVE',
  'AWAITING_FINALIZATION',
  'PREPARATION_FAILED',
  'FINALIZATION_FAILED',
  'COMPLETED',
  'DECLINED',
  'VOIDED',
  'EXPIRED',
]

/**
 * The request states each narrowing view lists. A failed preparation or finalization still needs
 * the sender, so it is "waiting" rather than closed. All lists every state (`REQUEST_STATES`).
 */
export const VIEW_STATES: Readonly<Partial<Record<AgreementView, readonly EnvelopeState[]>>> = {
  waiting: [
    'PREPARING',
    'ACTIVE',
    'AWAITING_FINALIZATION',
    'PREPARATION_FAILED',
    'FINALIZATION_FAILED',
  ],
  completed: ['COMPLETED'],
  closed: ['DECLINED', 'VOIDED', 'EXPIRED'],
}

/** The request states a view lists, or `null` when it lists no requests. */
export function viewRequestStates(view: AgreementView): readonly EnvelopeState[] | null {
  if (view === 'all') return REQUEST_STATES
  return VIEW_STATES[view] ?? null
}

/**
 * The status word for each state. It comes from the one shared vocabulary (`status-words.ts`), so
 * a request reads the same in a list, on Home and on its own page.
 */
export const ENVELOPE_STATE_KEY: Readonly<Record<EnvelopeState, MessageKey>> = Object.fromEntries(
  (['LOCAL_DRAFT', ...REQUEST_STATES] as const).map((state) => [
    state,
    requestStatusWord(state).key,
  ]),
) as Record<EnvelopeState, MessageKey>

/** Tones come from the same vocabulary, which uses the shared meaning map (UX-32). */
export function envelopeStateTone(state: EnvelopeState): Tone {
  return requestStatusWord(state).tone
}

/** The local document behind a request, when this device prepared it (`draft_<document id>`). */
export function requestDocumentId(summary: EnvelopeSummary): string | null {
  const draft = summary.draft_id
  return draft !== undefined && draft.startsWith('draft_') ? draft.slice('draft_'.length) : null
}

export const REQUEST_PAGE_SIZE = 25

/**
 * The cursor key for a read across every state. All asks the service once without a state filter
 * rather than once per state, so its page is one ordered list and needs one cursor.
 */
const ANY_STATE = 'ANY'

/** One read position per state (or `ANY`): `undefined` before the first page, `null` once exhausted. */
export type RequestCursors = Readonly<
  Partial<Record<EnvelopeState | typeof ANY_STATE, string | null>>
>

export type RequestPageOutcome =
  | {
      readonly kind: 'READY'
      readonly items: readonly EnvelopeSummary[]
      readonly cursors: RequestCursors
    }
  | { readonly kind: 'SIGNED_OUT' }
  | { readonly kind: 'FAILED'; readonly code: string }

/** Whether a set of states is every request state, so one unfiltered read covers it. */
function coversEveryState(states: readonly EnvelopeState[]): boolean {
  return REQUEST_STATES.every((state) => states.includes(state))
}

/** The read positions a set of states is paged by. */
function cursorKeys(
  states: readonly EnvelopeState[],
): readonly (EnvelopeState | typeof ANY_STATE)[] {
  return coversEveryState(states) ? [ANY_STATE] : states
}

/**
 * Reads the next page of every listed state that still has rows and merges them newest first. The
 * service filters by one state at a time, so a view over several states reads each in parallel and
 * keeps a cursor per state; a view over every state reads once, unfiltered. Nothing is read again.
 */
export async function loadRequestPage(input: {
  readonly transport: ApiTransport
  readonly states: readonly EnvelopeState[]
  readonly cursors: RequestCursors
  readonly limit?: number
}): Promise<RequestPageOutcome> {
  const limit = input.limit ?? REQUEST_PAGE_SIZE
  const pending = cursorKeys(input.states).filter((key) => input.cursors[key] !== null)
  try {
    const pages = await Promise.all(
      pending.map(async (key) => {
        const cursor = input.cursors[key]
        const query = new URLSearchParams(
          key === ANY_STATE ? { limit: String(limit) } : { state: key, limit: String(limit) },
        )
        if (cursor !== undefined && cursor !== null) query.set('cursor', cursor)
        const response = await input.transport.request<unknown>({
          method: 'GET',
          path: `/v1/envelopes?${query.toString()}`,
        })
        const parsed = EnvelopeListResponse.safeParse(response)
        if (!parsed.success)
          throw Object.assign(new Error('invalid'), { code: 'OP_INVALID_PAYLOAD' })
        return { key, page: parsed.data }
      }),
    )
    const cursors: Partial<Record<EnvelopeState | typeof ANY_STATE, string | null>> = {
      ...input.cursors,
    }
    for (const { key, page } of pages) cursors[key] = page.next_cursor
    return {
      kind: 'READY',
      // An unfiltered read can include a draft the service holds before activation; it is not a
      // request anyone was sent.
      items: sortRequests(
        pages.flatMap(({ page }) => page.items).filter((item) => item.state !== 'LOCAL_DRAFT'),
      ),
      cursors,
    }
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return { kind: 'SIGNED_OUT' }
    return { kind: 'FAILED', code: (error as { code?: string }).code ?? 'OP_SERVICE_UNAVAILABLE' }
  }
}

/** The moment a row is about: when it finished, else when it was sent, else when it was made. */
export function requestTimestamp(summary: EnvelopeSummary): string {
  return summary.completed_at ?? summary.activated_at ?? summary.created_at
}

export function sortRequests(items: readonly EnvelopeSummary[]): EnvelopeSummary[] {
  return [...items].sort(
    (a, b) => Date.parse(requestTimestamp(b)) - Date.parse(requestTimestamp(a)),
  )
}

export function hasMoreRequests(
  states: readonly EnvelopeState[],
  cursors: RequestCursors,
): boolean {
  return cursorKeys(states).some((key) => cursors[key] !== null && cursors[key] !== undefined)
}
